import { ChangeEvent, useState } from "react";
import toast from "react-hot-toast";
import { Col, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";

import { IActionPrompt } from "../../interfaces/configs";
import { ActionPromptEnum } from "../../interfaces/enums";
import { ActionPromptMap } from "../../utils/helpers/mappers";
import { Loading } from "../../utils/helpers/constants";
import { queryErrorHandler, toTitleCase } from "../../utils/helpers/logicHelper";
import {
  useApproveLoanRequestMutation,
  useRejectLoanRequestMutation,
  useApproveQuickPayRequestMutation,
  useRejectQuickPayRequestMutation,
  useRepayLoanRequestMutation,
  useRepayPartialLoanRequestMutation
} from "../../store/slices/backoffice.slice";
import { useRefundAdvancedPaymentRequestMutation } from "../../store/slices/dodge.slice";

type Props = {
  type: ActionPromptEnum,
  props?: string,
  confirmation?: string,
  onchange: () => void,
}

const ActionPromptModal = ({ type, props, confirmation, onchange }: Props) => {
  const actionProps: IActionPrompt = ActionPromptMap[type];
  const [show, setShow] = useState(true);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState<string>("");
  const [repaymentAmount, setRepaymentAmount] = useState<string>("");

  const [repayPartialLoanRequest] = useRepayPartialLoanRequestMutation();
  const [repayLoanRequest] = useRepayLoanRequestMutation();
  const [rejectLoanRequest] = useRejectLoanRequestMutation();
  const [approveLoanRequest] = useApproveLoanRequestMutation();
  const [rejectQuickPayRequest] = useRejectQuickPayRequestMutation();
  const [approveQuickPayRequest] = useApproveQuickPayRequestMutation();
  const [refundAdvancedPaymentrequest] = useRefundAdvancedPaymentRequestMutation();

  const isInvalid: boolean = (type === ActionPromptEnum.REFUND_ADVANCED_PAYMENT_REQUEST) ? !repaymentAmount : (type === ActionPromptEnum.APPROVE_QUICK_PAY_REQUEST || type === ActionPromptEnum.REJECT_QUICK_PAY_REQUEST) ? !text : text !== confirmation;


  const handleClose = (): void => {
    setShow(false);
    onchange();
  };

  const completeAction = () => {
    toast.success(actionProps.successMessage);
    if (type === ActionPromptEnum.APPROVE_QUICK_PAY_REQUEST || type === ActionPromptEnum.REJECT_QUICK_PAY_REQUEST) {

      setTimeout(() => {
        window.location.reload();
      }, 800);
      return;
    }

    handleClose();
  }

  const handleChangeRequest = async (event: any): Promise<void> => {
    setLoading(true);
    event.preventDefault();

    try {
      let responseData;

      if (props && type) {
        if (type === ActionPromptEnum.REPAY_PARTIAL_TOP_UP_REQUEST) responseData = await repayPartialLoanRequest({ id: props, amount: repaymentAmount }).unwrap();
        if (type === ActionPromptEnum.REPAY_COMPLETE_TOP_UP_REQUEST) responseData = await repayLoanRequest(props).unwrap();
        if (type === ActionPromptEnum.REJECT_TOP_UP_REQUEST) responseData = await rejectLoanRequest(props).unwrap();
        if (type === ActionPromptEnum.APPROVE_TOP_UP_REQUEST) responseData = await approveLoanRequest(props).unwrap();
        if (type === ActionPromptEnum.REJECT_QUICK_PAY_REQUEST) responseData = await rejectQuickPayRequest({ id: props, comment: text }).unwrap();
        if (type === ActionPromptEnum.APPROVE_QUICK_PAY_REQUEST) responseData = await approveQuickPayRequest({ id: props, comment: text }).unwrap();
        if (type === ActionPromptEnum.REFUND_ADVANCED_PAYMENT_REQUEST) responseData = await refundAdvancedPaymentrequest({ reference: props, amount: repaymentAmount }).unwrap();
      } else handleClose();

      const status =
        responseData?.data?.status ??
        responseData?.status ??
        null;

      if (status === Loading.NO_CONTENT) completeAction();
      else if (status === Loading.SUCCESS) {
        completeAction(); console.log(responseData.status);
      }
      else handleClose();
    } catch (err: any) {
      const errMessage = queryErrorHandler(toTitleCase(type), err);
      toast.error(errMessage);
    }

    setLoading(false);
  }

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton><Modal.Title>{actionProps.title}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12}>
            <p>{actionProps.message} - <b>{confirmation}</b></p>
            <p className="mb-3">
              You can send in a support request about this information, and may choose not to continue with this action.
            </p>
            <p className="mb-3 text-danger fw-bold">This action cannot be undone</p>
            <div className="mb-5">
              {(type === ActionPromptEnum.REPAY_PARTIAL_TOP_UP_REQUEST || type === ActionPromptEnum.REFUND_ADVANCED_PAYMENT_REQUEST) &&
                <input type="text" className="form-control ht-45-f mb-3" value={repaymentAmount} placeholder={`Enter amount you'd like to ${ type === ActionPromptEnum.REFUND_ADVANCED_PAYMENT_REQUEST ? "refund" : "repay"}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setRepaymentAmount(event.target.value)} />
              }

              { type !== ActionPromptEnum.REFUND_ADVANCED_PAYMENT_REQUEST &&
                <input type="text" className="form-control ht-45-f" value={text} placeholder={actionProps.placeholder}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setText(event.target.value)} />
              }
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-danger" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn btn-success" disabled={isInvalid} onClick={handleChangeRequest}>
                {actionProps.actionText} {loading && <i className="bx bx-loader-circle bx-spin" />}
              </button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}
export default ActionPromptModal;
